<script setup lang="ts">
import { computed, ref } from 'vue';
import type { HeroProfile, EffectiveStats } from '../game/hero-api';
import { SLOTS, type Inventory, type Artifact, type EquipSlot } from '../game/inventory-api';
import { itemThumb } from '../game/art';
import { STAT_META, SLOT_LABELS, scaledStats } from '../game/item-math';
import { CLASS_ABILITY, UNIVERSAL_MOVES } from '../game/class-ability';

/**
 * DOSSIER — the right column. A "confidential file" read-out of the hero's
 * combat totals, paperdoll, set bonuses, class signature move and active
 * boosts. Purely a display of stats/inventory props owned by HeroScreen.
 */
const props = defineProps<{ hero: HeroProfile; stats: EffectiveStats | null; inventory: Inventory | null }>();
const emit = defineEmits<{ (e: 'inspect', artifact: Artifact, slot: EquipSlot): void }>();

/** Teleported slot tooltip — anchored to the hovered doll, clamped on-screen. */
const tip = ref<{ a: Artifact; x: number; y: number; below: boolean } | null>(null);
function showTip(slot: EquipSlot, e: Event) {
  const a = equippedIn(slot);
  if (!a) { tip.value = null; return; }
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  // centred over the slot, clamped so edge slots never push the card off-screen
  // (78 ≈ half its min-width + margin); flips below when the slot is too close
  // to the top for the card (~120px worst case) to fit above.
  const x = Math.min(Math.max(r.left + r.width / 2, 78), window.innerWidth - 78);
  const below = r.top < 130;
  tip.value = { a, x, y: below ? r.bottom + 8 : r.top - 8, below };
}
function hideTip() { tip.value = null; }

const tiles = computed(() => {
  const eff = props.stats?.effective; const base = props.stats?.base; const gear = props.stats?.gear;
  const h = props.hero.hero.stats;
  const combat = (['attack', 'defense', 'magic', 'speed'] as const).map((k) => ({
    key: k,
    label: STAT_META[k].label,
    color: STAT_META[k].color,
    total: eff?.[k] ?? h[k],
    base: base?.[k] ?? h[k],
    gear: k === 'speed' ? (gear?.speed ?? 0) : (gear?.[k] ?? 0),
  }));
  const health = {
    key: 'hp' as const, label: 'Health', color: 'var(--color-success)',
    total: eff?.maxHp ?? h.maxHp, base: base?.maxHp ?? h.maxHp, gear: gear?.hp ?? 0,
  };
  const nerve = {
    key: 'nerve' as const, label: 'Nerve', color: 'var(--color-gold)',
    total: props.hero.hero.nerve.current, base: props.hero.hero.nerve.current, gear: 0,
    sub: `of ${props.hero.hero.nerve.max} max`,
  };
  return [...combat, health, nerve];
});

function equippedIn(slot: EquipSlot): Artifact | null {
  return props.inventory?.equipped[slot] ?? null;
}
function contributions(a: Artifact | null) {
  if (!a) return [];
  const s = scaledStats(a);
  const keys: (keyof typeof s)[] = ['attack', 'defense', 'magic', 'speed', 'hp'];
  return keys
    .filter((k) => s[k] !== 0)
    .map((k) => ({ label: k === 'hp' ? 'Health' : STAT_META[k as 'attack'].label, val: s[k] }));
}
function onSlotClick(slot: EquipSlot) {
  const a = equippedIn(slot);
  if (a) emit('inspect', a, slot);
}

const ability = computed(() => CLASS_ABILITY[props.hero.hero.class]);

// Maneuvers are paged one-at-a-time (dots) so the ability card keeps a fixed
// height — no scroll no matter how long the move descriptions run.
const moveIdx = ref(0);
const move = computed(() => UNIVERSAL_MOVES[moveIdx.value]);
</script>

<template>
  <div class="flex flex-col gap-2 min-h-0 hd-scroll">
    <!-- STAT TILES -->
    <div class="corner-frame relative rounded-xl border border-border bg-surface p-3 shrink-0">
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <div class="hd-h mb-2">Bureau file — combat dossier</div>
      <div class="grid grid-cols-2 gap-2.5">
        <div v-for="t in tiles" :key="t.key" class="hd-tile">
          <span class="hd-tile-underline" :style="{ background: `linear-gradient(90deg, ${t.color}, transparent)` }"></span>
          <div class="hd-tile-label">{{ t.label }}</div>
          <div class="hd-tile-total" :style="{ color: t.color }">{{ t.total.toLocaleString() }}</div>
          <div v-if="t.gear" class="hd-tile-break">{{ t.base }} <span class="text-gold">+{{ t.gear }}</span></div>
          <div v-else-if="'sub' in t" class="hd-tile-break">{{ (t as { sub: string }).sub }}</div>
        </div>
      </div>
    </div>

    <!-- PAPERDOLL. Hidden on the single-column layout (<=1100px): HeroCard's
         EQUIPPED strip stacks right above this panel there, and two paperdolls
         back to back read as a bug. Desktop keeps both — they live in separate
         columns and this one carries the stat-contribution tooltips. -->
    <div class="hd-paperdoll corner-frame relative rounded-xl border border-border bg-surface p-3 shrink-0">
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <div class="hd-h mb-2">Loadout</div>
      <div class="grid grid-cols-6 gap-1.5">
        <div v-for="slot in SLOTS" :key="slot" class="hd-doll-wrap">
          <button
            class="hd-doll"
            :title="equippedIn(slot) ? undefined : SLOT_LABELS[slot]"
            :style="{ '--rar': equippedIn(slot) ? '#c8a24c' : 'transparent' }"
            @click="onSlotClick(slot)"
            @mouseenter="showTip(slot, $event)"
            @mouseleave="hideTip"
            @focus="showTip(slot, $event)"
            @blur="hideTip"
          >
            <img :src="itemThumb(slot.toUpperCase(), equippedIn(slot)?.rarity, equippedIn(slot)?.name)" :alt="slot"
                 class="w-full h-full object-cover rounded" :class="{ 'opacity-25': !equippedIn(slot) }" />
          </button>
        </div>
      </div>
    </div>

    <!-- Slot tooltip. Teleported: the dossier column scrolls (.hd-scroll) inside
         the viewport-locked profile (overflow:hidden), so a bubble positioned
         inside the wrap was clipped by both — edge slots lost half the card, top
         rows lost it entirely. Fixed-position at body level escapes every
         overflow; the mouseenter handler anchors + clamps it to the viewport. -->
    <Teleport to="body">
      <div v-if="tip" class="hd-tooltip" :class="{ 'hd-tooltip--below': tip.below }" :style="{ left: tip.x + 'px', top: tip.y + 'px' }">
        <div class="font-heading text-gold-bright text-xs mb-1">{{ tip.a.name }}</div>
        <div v-for="c in contributions(tip.a)" :key="c.label" class="flex justify-between gap-3 text-[11px]">
          <span class="text-text-dim">{{ c.label }}</span>
          <span class="text-gold">+{{ c.val }}</span>
        </div>
      </div>
    </Teleport>

    <!-- SET BONUSES -->
    <div class="corner-frame relative rounded-xl border border-border bg-surface p-3 shrink-0">
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <div class="hd-h mb-2.5">Set bonuses</div>
      <template v-if="stats?.sets.length">
        <div v-for="s in stats.sets" :key="s.name" class="mb-2 last:mb-0 flex items-start gap-2">
          <span class="hd-dot"></span>
          <div>
            <div class="font-heading text-gold capitalize text-sm">{{ s.name }} <span class="text-text-dim font-body text-xs">· {{ s.pieces }}pc</span></div>
            <div class="font-body text-xs text-text-dim italic leading-snug">{{ s.description }}</div>
          </div>
        </div>
      </template>
      <p v-else class="font-body text-xs text-text-dim italic">Equip 2+ pieces of a set to activate its bonus.</p>
    </div>

    <!-- CLASS ABILITY -->
    <div class="corner-frame relative rounded-xl border border-border bg-surface p-3 shrink-0">
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <div class="hd-h mb-2.5">Class ability</div>
      <div class="font-heading text-gold-bright text-base">{{ ability.name }}</div>
      <div class="font-body text-xs text-text-dim mb-2 hd-ability-desc">{{ ability.desc }} <span class="text-gold">· {{ ability.cooldown }}-round cooldown</span></div>
      <div class="border-t border-border pt-2">
        <div class="flex items-center justify-between mb-1.5">
          <span class="hd-h">Maneuvers</span>
          <span class="hd-pager">
            <button
              v-for="(m, i) in UNIVERSAL_MOVES" :key="m.name"
              class="hd-pdot" :class="{ on: i === moveIdx }"
              :title="m.name" :aria-label="m.name" @click="moveIdx = i"
            ></button>
          </span>
        </div>
        <div class="hd-move">
          <span class="font-label uppercase tracking-wider text-gold text-[11px]">{{ move.name }}</span>
          <p class="font-body text-[11px] text-text-dim leading-snug mt-0.5">{{ move.desc }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hd-h {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.16em;
  font-size: 11px; color: var(--color-text-dim, #8a8378);
}
.hd-tile {
  position: relative; overflow: hidden;
  border: 1px solid var(--color-border, #2a2436);
  border-radius: 8px;
  padding: 0.45rem 0.6rem 0.5rem;
  background: rgba(0,0,0,0.18);
}
.hd-tile-underline { position: absolute; left: 0; right: 0; bottom: 0; height: 2px; }
.hd-tile-label { font-family: var(--font-label, sans-serif); text-transform: uppercase; letter-spacing: 0.1em; font-size: 9.5px; color: var(--color-text-dim, #8a8378); }
.hd-tile-total { font-family: var(--font-heading, serif); font-size: 1.2rem; line-height: 1.15; font-variant-numeric: tabular-nums; }
/* height-locked profile: if a short viewport can't hold every panel, the
   dossier column scrolls quietly instead of the page */
.hd-scroll {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--color-gold) 35%, transparent) transparent;
}
.hd-tile-break { font-family: var(--font-body, serif); font-size: 10.5px; color: var(--color-text-dim, #8a8378); }

.hd-doll-wrap { position: relative; display: flex; flex-direction: column; align-items: center; gap: 3px; }
/* single-column profile: HeroCard's EQUIPPED strip is right above — one paperdoll is enough */
@media (max-width: 1100px) { .hd-paperdoll { display: none; } }
.hd-doll {
  width: 100%; aspect-ratio: 1; border-radius: 8px; padding: 3px;
  border: 1px solid color-mix(in srgb, var(--rar, transparent) 55%, var(--color-border, #2a2436));
  background: rgba(0,0,0,0.25);
  transition: border-color 130ms ease, box-shadow 130ms ease;
}
.hd-doll:hover { border-color: var(--color-gold, #c9a84c); box-shadow: 0 0 10px color-mix(in srgb, var(--color-gold, #c9a84c) 45%, transparent); }

/* Teleported to <body> (see template comment): fixed so no ancestor overflow
   can clip it. Anchored above the slot (bottom edge at `top`), flipped under
   it near the viewport top. Scoped styles still apply through Teleport. */
.hd-tooltip {
  position: fixed; z-index: 90;
  min-width: 140px;
  transform: translate(-50%, -100%);
  background: var(--color-surface-2, #100e14);
  border: 1px solid var(--color-gold, #c9a84c);
  border-radius: 8px;
  padding: 8px 10px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.55);
  pointer-events: none;
  animation: hd-tip-in 130ms ease;
}
.hd-tooltip--below { transform: translate(-50%, 0); }
@keyframes hd-tip-in { from { opacity: 0; } }
.hd-dot { width: 6px; height: 6px; margin-top: 5px; border-radius: 999px; background: var(--color-gold, #c9a84c); box-shadow: 0 0 6px var(--color-gold, #c9a84c); flex-shrink: 0; }
/* Class-ability maneuver pager — one move per page, fixed height (no scroll) */
.hd-ability-desc {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.hd-pager { display: inline-flex; gap: 6px; align-items: center; }
.hd-pdot {
  width: 7px; height: 7px; padding: 0; border-radius: 999px; cursor: pointer;
  border: 1px solid color-mix(in srgb, var(--color-gold, #c9a84c) 55%, transparent);
  background: transparent; transition: background 130ms ease, box-shadow 130ms ease;
}
.hd-pdot.on { background: var(--color-gold, #c9a84c); box-shadow: 0 0 6px color-mix(in srgb, var(--color-gold, #c9a84c) 60%, transparent); }
.hd-pdot:hover { border-color: var(--color-gold, #c9a84c); }
.hd-move { min-height: 42px; }
</style>
